/**
 * Page-state persistence for "navigate to edit → back" flows.
 *
 * Call snapshotPage() immediately before navigating to the edit screen.
 * Call popPageSnapshot() inside a useEffect([]) on the page that should
 * restore its state when the user returns.
 */

const PREFIX = 'pb-snap'

interface Snapshot<T> {
  state: T
  scroll: number
}

/** Save current state + scroll position to sessionStorage. */
export function snapshotPage<T>(pageKey: string, state: T): void {
  try {
    const snap: Snapshot<T> = { state, scroll: Math.round(window.scrollY) }
    sessionStorage.setItem(`${PREFIX}:${pageKey}`, JSON.stringify(snap))
  } catch {
    // quota exceeded – silently ignore
  }
}

/**
 * Retrieve and remove the snapshot for the given page.
 * Returns null if no snapshot exists (i.e. user didn't come from an edit).
 */
export function popPageSnapshot<T>(pageKey: string): Snapshot<T> | null {
  try {
    const raw = sessionStorage.getItem(`${PREFIX}:${pageKey}`)
    if (!raw) return null
    sessionStorage.removeItem(`${PREFIX}:${pageKey}`)
    return JSON.parse(raw) as Snapshot<T>
  } catch {
    return null
  }
}

/**
 * Scroll the window to `top` after the next two animation frames
 * (ensures the browser has painted the full page before scrolling).
 */
export function restoreScroll(top: number): void {
  if (top <= 0) return
  requestAnimationFrame(() =>
    requestAnimationFrame(() =>
      window.scrollTo({ top, behavior: 'instant' })
    )
  )
}
